import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { Image, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { useAppSelector } from 'store/hooks';
import { AppView, Button, CardView, Spacer } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import LogoBankAccount from 'design-system/LogoBankAccount';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { useConnectedAccount } from 'features/invest/hooks/selectors';
import { selectAutosaveRulesArr } from 'features/pots/reducers/selectors';
import { selectConnectedBankAccount } from 'features/wealth/selectors';
import { selectFeatureFlag } from 'reducers/selectors';
import Amplitude from 'utils/amplitude';
import { getBalance } from 'utils/api/getBalance';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount, formatSortCode } from 'utils/formatting';
import links from 'utils/links';
import Modal from 'utils/packages/Modal';
const directDebitLogo = require('../../img/directDebitLogo.png');
export const getFooterText = (providerName) => `This Guarantee is offered by all banks and building societies that accept instructions to pay Direct Debits.

If there are any changes to the amount, date or frequency of your Direct Debit ${providerName} will notify you 3 working days in advance of your account being debited or as otherwise agreed. If you request ${providerName} to collect a payment, confirmation of the amount and date will be given to you at the time of the request.

If an error is made in the payment of your Direct Debit, by ${providerName} or your bank or building society, you are entitled to a full and immediate refund of the amount paid from your bank or building society. If you receive a refund you are not entitled to, you must pay it back when ${providerName} asks you to. 

You can cancel a Direct Debit at any time by simply contacting your bank or building society. Written confirmation may be required. Please also notify us.`;
const AutosaveSelectBank = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const styles = useStyles(styleSet);
    const bankAccountFromProps = route.params?.bankAccount;
    const connectedAccount = useConnectedAccount();
    const postedRules = useAppSelector(selectAutosaveRulesArr);
    const connectedBankAccount = useAppSelector(selectConnectedBankAccount);
    const autosaveId = useAppSelector((state) => state.pots.savingsPlan?.id);
    const allowOverdraft = useAppSelector((state) => state.pots.savingsPlan?.allowOverdraft);
    const autosaveDistribution = useAppSelector((state) => state.pots.savingsPlan?.distribution);
    const collectionFrequency = useAppSelector((state) => state.pots.savingsPlan?.collectionFrequency);
    const directDebitFlag = useAppSelector((store) => selectFeatureFlag(store, 'direct_debit_provider'));
    const directDebitInfo = useMemo(() => {
        if (directDebitFlag.extra?.providerName &&
            directDebitFlag.extra?.providerPrivacyUrl &&
            directDebitFlag.extra?.transactionMemo) {
            return {
                providerName: directDebitFlag.extra.providerName,
                providerPrivacyUrl: directDebitFlag.extra.providerPrivacyUrl,
            };
        }
        return {
            providerName: 'GoCardless',
            providerPrivacyUrl: 'https://gocardless.com/privacy/',
        };
    }, [
        directDebitFlag.extra?.providerName,
        directDebitFlag.extra?.providerPrivacyUrl,
        directDebitFlag.extra?.transactionMemo,
    ]);
    const linkedAccount = useMemo(() => {
        if (connectedBankAccount) {
            return connectedBankAccount;
        }
        if (connectedAccount) {
            return connectedAccount;
        }
        if (bankAccountFromProps) {
            return bankAccountFromProps;
        }
        return undefined;
    }, [bankAccountFromProps, connectedAccount, connectedBankAccount]);
    const [primaryBalance] = linkedAccount ? getBalance(linkedAccount) : [undefined];
    const hasLinkedAccount = !!linkedAccount;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING}/>,
        });
    }, []);
    useEffect(() => {
        Amplitude.logEvent('Confirm Direct Debit Account', { type: 'autosave' });
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT, paddingHorizontalStyle);
    const handleContinue = useCallback(async () => {
        if (linkedAccount) {
            if (!('ukSortCode' in linkedAccount)) {
                if (linkedAccount?.originalType === 'SAVINGS') {
                    Modal.showConfirmation({
                        title: 'Direct Debit isn’t available',
                        subtitle: 'Your funding account is a savings account, which doesn‘t support Direct Debit. To change your funding account, please get in touch with our support team.',
                        confirmButtonTitle: 'Okay',
                        cancelButtonTitle: 'Contact support',
                        onPressCancel: () => {
                            navigation.navigate('Support');
                        },
                    });
                    return;
                }
                if (route.params?.distribution && typeof route.params?.overdraft === 'boolean' && route.params?.rules) {
                    navigation.navigate('AutosaveConfirmBankDetails', {
                        rules: route.params.rules,
                        bankAccount: linkedAccount,
                        overdraft: route.params?.overdraft,
                        distribution: route.params?.distribution,
                        collectionFrequency: route.params.collectionFrequency,
                    });
                }
                else if (autosaveId && typeof allowOverdraft === 'boolean' && autosaveDistribution && collectionFrequency) {
                    navigation.navigate('AutosaveConfirmBankDetails', {
                        rules: postedRules,
                        collectionFrequency,
                        overdraft: allowOverdraft,
                        bankAccount: linkedAccount,
                        distribution: autosaveDistribution,
                    });
                }
            }
        }
    }, [
        allowOverdraft,
        autosaveDistribution,
        autosaveId,
        collectionFrequency,
        linkedAccount,
        postedRules,
        route.params?.collectionFrequency,
        route.params?.distribution,
        route.params?.overdraft,
        route.params?.rules,
    ]);
    const openPrivacyNotice = useCallback(() => {
        links.openLink(directDebitInfo.providerPrivacyUrl);
    }, [directDebitInfo.providerPrivacyUrl]);
    const formattedSortCode = useMemo(() => {
        if (!linkedAccount)
            return '';
        if ('sortCode' in linkedAccount) {
            return formatSortCode(linkedAccount.sortCode);
        }
        if ('ukSortCode' in linkedAccount) {
            return formatSortCode(linkedAccount.ukSortCode);
        }
        return '';
    }, [linkedAccount]);
    const accountNumber = useMemo(() => {
        if (!linkedAccount)
            return '';
        if ('accountNumber' in linkedAccount) {
            return linkedAccount.accountNumber;
        }
        return linkedAccount.ukAccountNumber;
    }, [linkedAccount]);
    const accountName = useMemo(() => {
        if (linkedAccount) {
            if ('accountNumber' in linkedAccount) {
                return linkedAccount.name;
            }
            return linkedAccount.bankName || 'Linked bank';
        }
        return 'Linked bank';
    }, [linkedAccount]);
    const isContinueDisabled = !(hasLinkedAccount && !('ukSortCode' in linkedAccount));
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="Set up your Direct Debit" subTitle="We’ll use this account to collect payments via Direct Debit"/>

          {!!hasLinkedAccount && (<CardView noMarginTop>
              <View style={styles.fundingAccountCard}>
                <View style={styles.row}>
                  <View style={styles.iconContainer}>
                    <LogoBankAccount size="R44" onCard data={linkedAccount}/>
                  </View>
                  <Spacer w={16}/>
                  <View style={styles.flex}>
                    <Text Text-12 Primary>
                      FUNDING ACCOUNT
                    </Text>
                    <Text Text-16 numberOfLines={1}>
                      {accountName}
                    </Text>
                    <Text TextThin-14 Secondary>
                      {formattedSortCode} {accountNumber}
                    </Text>
                  </View>
                  {primaryBalance ? (<Text Numbers-16>{formatAmount(primaryBalance.amount, primaryBalance.currency, 2)}</Text>) : null}
                </View>
              </View>
            </CardView>)}

          <CardView withPadding noMarginTop>
            <View>
              <Text TextThin-14 Secondary>
                Payments by “{directDebitInfo.providerName}”. Read the{' '}
                <Text TextThin-14 Primary onPress={openPrivacyNotice} suppressHighlighting>
                  {directDebitInfo.providerName} privacy notice.
                </Text>
              </Text>
            </View>
            <Spacer.H8 />
            <View style={styles.footer}>
              <View style={styles.flex}>
                <Text TextThin-14 Secondary>
                  Your payments are protected by the Direct Debit Guarantee.
                </Text>
              </View>
              <Spacer.W16 />
              <Image source={directDebitLogo} style={styles.img}/>
            </View>
            <View style={styles.footerText}>
              <Text TextThin-14 Secondary>
                {getFooterText(directDebitInfo.providerName)}
              </Text>
            </View>
          </CardView>
          <Spacer.H16 />
        </Animated.ScrollView>
        <Button floating brand title="Continue" onPress={handleContinue} disabled={isContinueDisabled}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    fundingAccountCard: {
        padding: rem(16),
    },
    iconContainer: {
        width: rem(44),
        height: rem(44),
        alignItems: 'center',
        borderRadius: rem(22),
        justifyContent: 'center',
        borderWidth: rem(1),
        borderColor: colors.borders.logoStroke,
        backgroundColor: colors.background.contrast,
    },
    footer: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    footerText: {
        paddingTop: rem(8),
    },
    flex: {
        flex: 1,
    },
    img: {
        width: rem(80),
        height: rem(80),
        tintColor: colors.text.primary,
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
}));
export default AutosaveSelectBank;
